if (!customElements.get("search-modal")) {
  class SearchModal extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.searchModal = null;
      this.form = null;
      this.input = null;
      this.url = "";
      this.predictiveSearchResults = null;
      this.isModalOpen = false;
      this.search_url = "/search/";
      this.predictive_search_url = "/search/suggest";
      this.locale = "";
      this.searchTerm = "";
      this.handleResize = null;
      this.resultsResizeObserver = null;
    }

    connectedCallback() {
      this.searchButton = document.querySelector(".search-button");
      this.searchModal = this.querySelector(".search-modal");
      this.searchCloseButton = this.querySelector(".search-modal__close-button");
      this.form = this.querySelector("form.search");
      this.input = this.form.querySelector('input[type="search"]');
      this.predictiveSearchResults = document.querySelector("[data-predictive-search]");

      const localeRoot = this.searchModal.getAttribute("data-locale-root");
      if (localeRoot !== "/") this.locale = localeRoot;

      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleResize = this.updateResultsMaxHeight.bind(this);
      this.resultsResizeObserver = new ResizeObserver(() => {
        this.updateResultsMaxHeight();
      });

      window.addEventListener("resize", this.handleResize);
      this.initEventListeners();
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.handleResize);
      this.resultsResizeObserver?.disconnect();
    }

    initEventListeners() {
      this.searchButton.addEventListener("click", (e) => {
        e.stopPropagation();
        this.openModal();
      });

      this.searchCloseButton.addEventListener("click", () => this.closeModal());
      this.input?.addEventListener("input", () => this.onChange());
      this.trendingTerms = this.querySelectorAll(".search-modal__trending-link");

      this.trendingTerms.forEach(button => {
        button.addEventListener("click", (e) => {
          const term = e.currentTarget.getAttribute("data-term");
          if (term && this.input) {
            this.input.value = term;
            this.onChange();
          }
        });
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.closeModal();
      });

      document.addEventListener("click", this.handleOutsideClick);
    }

    handleOutsideClick(event) {
      if (!this.isModalOpen) return;

      if (
        !this.searchModal.contains(event.target) &&
        !this.searchButton.contains(event.target)
      ) {
        this.closeModal();
      }
    }

    openModal() {
      this.searchModal.setAttribute("open", "");
      this.searchButton.setAttribute("aria-expanded", "true");
      this.isModalOpen = true;
      this.bodyLock(true);

      requestAnimationFrame(() => {
        this.updateResultsMaxHeight();
        const results = this.querySelector(".search-modal__results");
        if (results) this.resultsResizeObserver.observe(results);
        this.input?.focus({ preventScroll: true });
      });
    }

    closeModal() {
      this.searchModal.removeAttribute("open");
      this.searchButton.setAttribute("aria-expanded", "false");
      this.isModalOpen = false;
      this.clearValue();
      this.predictiveSearchResults.innerHTML = "";
      this.resultsResizeObserver?.disconnect();
      this.bodyLock(false);
    }

    updateResultsMaxHeight() {
      const results = this.querySelector(".search-modal__results");
      if (!results) return;

      const rect = results.getBoundingClientRect();
      const maxHeight = window.innerHeight - rect.top;

      if (maxHeight > 0) {
        results.style.maxHeight = `${maxHeight}px`;
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    clearValue() {
      this.input.value = "";
    }

    onChange() {
      this.searchTerm = this.getQuery();
      this.getSearchResults(this.searchTerm);
    }

    getSearchResults(searchTerm) {
      if (!searchTerm.length) {
        this.predictiveSearchResults.innerHTML = "";
        return;
      }

      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.url = `${this.search_url}?q=${encodeURIComponent(searchTerm)}`;

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      const sectionId = "predictive-search";
      const searchUrl = `${this.locale}${this.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&${encodeURIComponent("resources[type]")}=product,page,article&${encodeURIComponent(
        "resources[options][fields]"
      )}=title,product_type,variants.sku,tag,vendor,body&section_id=${sectionId}`;


      fetch(searchUrl)
        .then((r) => r.text())
        .then((text) => {
          const html = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector(`#shopify-section-${sectionId}`).innerHTML;

          this.cachedResults[queryKey] = html;
          this.renderSearchResults(html);

          // Fetch real total count from main search
          fetch(`${this.locale}${this.search_url}?q=${encodeURIComponent(searchTerm)}`)
            .then(res => res.text())
            .then(fullText => {
              const fullHtml = new DOMParser().parseFromString(fullText, "text/html");
              const resultsText = fullHtml.querySelector('.main-search__results-text')?.textContent;
              if (resultsText) {
                const match = resultsText.match(/\((\d+)\)/);
                if (match && match[1]) {
                  const countEl = this.querySelector('[data-total-count]');
                  if (countEl) countEl.textContent = match[1];
                }
              }
            });
        })
        .catch(() => this.closeModal());
    }

    renderSearchResults(markup) {
      this.predictiveSearchResults.innerHTML = markup;
      requestAnimationFrame(() => this.updateResultsMaxHeight());
    }

    bodyLock(lock) {
      document.body.style.overflow = lock ? "hidden" : "";
    }
  }

  customElements.define("search-modal", SearchModal);
}
